import {
  Box,
  Button,
  CascaderField,
  Col,
  FieldLabel,
  Flex,
  Form,
  Row,
  theme,
} from '@/components';
import { useProjectFormContext } from '@/features/project/_hooks/form-context';
import type { sheetTypeEnum } from '@/utils/enum';
import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import type { FC } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useSelectFiledFormData } from '../../_hooks/project-form-handler';

type Props = {
  type: z.infer<typeof sheetTypeEnum>;
};

export const OtherResponsibleOrganizationsArrayField: FC<Props> = ({
  type,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const {
    control,
    formState: { errors },
    watch,
  } = useProjectFormContext();

  const [fiscalYear, fiscalYearOfSnapshot] = watch([
    'data.fiscalYear',
    'data.fiscalYearOfSnapshot',
  ]);

  const snapshotId = fiscalYearOfSnapshot?.at(-1);

  const { organizationOptions } = useSelectFiledFormData({
    inputFiscalYear: fiscalYear,
    inputSnapshotId: snapshotId,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'data.nestedOtherResponsibleOrganizationIds',
  });

  return (
    <Form.Item
      label={
        <FieldLabel
          text={t('other-organization-in-charge')}
          hint={
            type === 'KS'
              ? t('hint-other-organization-in-charge-for-ks')
              : t('hint-other-organization-in-charge')
          }
        />
      }
    >
      <Row gutter={[24, 8]}>
        {fields.map((field, idx) => (
          <Col key={field.id} span={12}>
            <Flex gap={8} align="baseline">
              <Box flex={1}>
                <CascaderField
                  name={`data.nestedOtherResponsibleOrganizationIds.${idx}`}
                  control={control}
                  errors={errors}
                  options={organizationOptions.data}
                  enableIntermediate
                  allowClear={false}
                  wrapperStyle={{ marginBottom: 0 }}
                  loading={organizationOptions.isPending}
                  textNotInOption={t('not-exist-in-organization-options')}
                />
              </Box>
              <CloseCircleOutlined
                style={{ color: token.colorPrimary, padding: 8, margin: -8 }}
                onClick={() => {
                  remove(idx);
                }}
              />
            </Flex>
          </Col>
        ))}
        <Col span={24}>
          <Row gutter={[24, 0]}>
            <Col span={12}>
              <Button
                type="dashed"
                onClick={() => {
                  append([[]]);
                }}
                block
                icon={<PlusOutlined />}
              >
                {t('add-other-organization-in-charge')}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form.Item>
  );
};
